import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';
import { AllocationBucketTypes } from '@/features/Collaborators/Models';
import {
  AvailabilityNeedTypes,
  AvailabilityStatuses,
  AvailabilityWorkModels,
} from './availability.types';

const timestampSchema = z
  .instanceof(Timestamp)
  .or(z.date().transform((value) => Timestamp.fromDate(value)));

const capabilitySchema = z.object({
  typeId: z
    .string({
      required_error: 'O tipo de capacidade é obrigatório.',
    })
    .min(1, 'Selecione um tipo de capacidade.'),
  typeName: z
    .string({
      required_error: 'O nome do tipo é obrigatório.',
    })
    .min(1, 'Informe o nome do tipo de capacidade.'),
  technologyId: z.string().nullable().optional(),
  technologyName: z.string().nullable().optional(),
  seniorityId: z.string().nullable().optional(),
  seniorityName: z.string().nullable().optional(),
  required: z.boolean(),
  isPrimary: z.boolean().optional().default(false),
});

const candidateSchema = z.object({
  collaboratorId: z
    .string({
      required_error: 'O identificador do colaborador é obrigatório.',
    })
    .min(1, 'Selecione um colaborador válido.'),
  collaboratorName: z
    .string({
      required_error: 'O nome do colaborador é obrigatório.',
    })
    .min(1, 'Informe o nome do colaborador.'),
  collaboratorAvatar: z.string().nullable().optional(),
  isChosen: z.boolean().optional(),
  addedAt: timestampSchema.nullable().optional(),
  matchScore: z.number().optional(),
  matchScores: z
    .object({
      financial: z.number(),
      availability: z.number(),
      location: z.number(),
      capabilities: z.number(),
    })
    .optional(),
  matchAssumptions: z
    .object({
      salaryMissing: z.boolean(),
      salaryLimitMissing: z.boolean(),
      availabilityMissing: z.boolean(),
      locationFallback: z.boolean(),
      optionalAutoFulfilled: z.boolean(),
    })
    .optional(),
  matchInputs: z.record(z.any()).optional(),
  matchContractType: z.string().nullable().optional(),
  matchNormalizedSalary: z.number().nullable().optional(),
  matchLocationCity: z.string().nullable().optional(),
  matchLocationState: z.string().nullable().optional(),
  capabilityDebug: z.record(z.any()).optional(),
  debug: z.record(z.any()).optional(),
});

export const AvailabilitySlotSchema = z.object({
  slotId: z
    .string({
      required_error: 'O identificador da vaga é obrigatório.',
    })
    .min(1, 'O identificador da vaga é obrigatório.'),
  title: z.string().nullable().optional(),
  bucketType: z.nativeEnum(AllocationBucketTypes, {
    required_error: 'Selecione o tipo de bucket.',
  }),
  bucketValue: z
    .number({
      required_error: 'Informe a quantidade de horas.',
      invalid_type_error: 'A quantidade de horas deve ser um número.',
    })
    .min(0, 'A quantidade de horas deve ser positiva.'),
  capabilities: z.array(capabilitySchema),
  workModel: z.nativeEnum(AvailabilityWorkModels, {
    required_error: 'Selecione o modelo de trabalho.',
  }),
  workCity: z.string().nullable().optional(),
  workState: z.string().nullable().optional(),
  acceptsPJ: z.boolean(),
  cltSalaryCeiling: z
    .number({
      invalid_type_error: 'O limite salarial deve ser um número.',
    })
    .nonnegative('O limite salarial deve ser positivo.')
    .nullable()
    .optional(),
  candidates: z.array(candidateSchema),
  chosenCollaboratorId: z.string().nullable().optional(),
  commentsInternal: z.string().nullable().optional(),
  lastSuggestionAt: timestampSchema.nullable().optional(),
  lastMatchFilters: z.record(z.any()).nullable().optional(),
  lastMatchResultsCount: z
    .number({
      invalid_type_error: 'O total de colaboradores encontrados deve ser um número.',
    })
    .int('Informe um número inteiro.')
    .min(0, 'O total de colaboradores deve ser positivo.')
    .nullable()
    .optional(),
  fulfillmentStrategy: z
    .enum(['known', 'assist', 'hire'])
    .optional()
    .default('known'),
}).superRefine((data, ctx) => {
  if (
    data.workModel === AvailabilityWorkModels.Onsite ||
    data.workModel === AvailabilityWorkModels.Hybrid
  ) {
    if (!data.workCity || !data.workCity.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['workCity'],
        message: 'Informe a cidade da atuação.',
      });
    }
    if (!data.workState || !data.workState.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['workState'],
        message: 'Informe o estado da atuação.',
      });
    }
  }
});

export const AvailabilityBaseSchema = z.object({
  clientId: z
    .string({
      required_error: 'Selecione um cliente.',
    })
    .min(1, 'Selecione um cliente.'),
  clientName: z
    .string({
      required_error: 'Informe o nome do cliente.',
    })
    .min(1, 'Informe o nome do cliente.'),
  clientLogo: z.string().nullable().optional(),
  initiative: z
    .string({
      required_error: 'Informe a iniciativa.',
    })
    .min(1, 'Informe a iniciativa.'),
  status: z.nativeEnum(AvailabilityStatuses, {
    required_error: 'Selecione o status.',
  }),
  needType: z.nativeEnum(AvailabilityNeedTypes, {
    required_error: 'Selecione o tipo de necessidade.',
  }),
  referenceDate: timestampSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
  slots: z
    .array(AvailabilitySlotSchema)
    .min(1, 'Adicione ao menos uma vaga.'),
  isInternalDemand: z.boolean().optional(),
  priorityLevel: z
    .number({
      invalid_type_error: 'A prioridade deve ser um número.',
    })
    .int()
    .min(0, 'A prioridade deve ser positiva.')
    .nullable()
    .optional(),
  createdAt: timestampSchema.optional(),
  createdById: z
    .string({
      required_error: 'Identificador do criador é obrigatório.',
    })
    .min(1, 'Identificador do criador é obrigatório.'),
  createdByName: z
    .string({
      required_error: 'Nome do criador é obrigatório.',
    })
    .min(1, 'Nome do criador é obrigatório.'),
  createdByAvatar: z.string().nullable().optional(),
  updatedAt: timestampSchema.nullable().optional(),
  updatedById: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  updatedByAvatar: z.string().nullable().optional(),
});

export const AvailabilityCreateSchema = AvailabilityBaseSchema.extend({
  createdAt: timestampSchema.optional(),
  updatedAt: timestampSchema.optional(),
});

export const AvailabilityUpdateSchema = AvailabilityBaseSchema.partial().extend({
  id: z
    .string({
      required_error: 'O identificador da disponibilidade é obrigatório.',
    })
    .min(1, 'O identificador da disponibilidade é obrigatório.'),
});

export type AvailabilitySlotInput = z.infer<typeof AvailabilitySlotSchema>;
export type AvailabilityBaseInput = z.infer<typeof AvailabilityBaseSchema>;
export type AvailabilityCreateInput = z.infer<typeof AvailabilityCreateSchema>;
export type AvailabilityUpdateInput = z.infer<typeof AvailabilityUpdateSchema>;
